import React, { Component, Suspense, lazy } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Route, Switch, withRouter, Redirect, matchPath } from 'react-router-dom';
import { getFormSyncErrors, getFormSubmitErrors, formValueSelector } from 'redux-form';
import { PrivateRoute, UnreleasedRoute } from './CustomRoutes'

import {
  loginUser,
  resetPassword,
  getUser,
  getViewingUser } from '../components/auth/auth-actions';
import { allowUnreleased } from '../index';

import { StyledContainer, StyledWrapper } from './appStyles';
import UnreleasedVisibility from './UnreleasedVisibility'

// constant components
import NavBar from '../components/NavBar/NavBar';
import Footer from '../components/Footer/Footer';
import FlashMessages from '../components/FlashMessages/FlashMessages';
import ViewOnlyBar from '../components/ViewOnly/ViewOnlyBar';
import Loader from '../common/components/Loader';

//Route Components
// Auth
const Login2 = lazy(() => import('../components/auth2/Login'));
const Register2 = lazy(() => import('../components/auth2/Register'));
const MoreDetails = lazy(() => import('../components/auth2/MoreDetailsForm'));
const InviteByEmail = lazy(() => import('../components/auth2/InviteByEmail'));
const ResetPassword = lazy(() => import('../components/auth/ResetPassword'));

// Current App
const NotFound = lazy(() => import('../components/NotFound/NotFound'));
const AccessError = lazy(() => import('../components/NotFound/AccessError'))
const Dashboard = lazy(() => import('../components/Dashboard/Dashboard'));
const Analytics = lazy(() => import('../components/Analytics/Analytics'));
const AccountPage = lazy(() => import('../components/MyAccount/MyAccount'));
const SamplesContainer = lazy(() => import('../components/SampleSubmission/SamplesContainer'));

// New App
const FieldManagementContainer = lazy(() => import('../components/FieldManagement/FieldManagementContainer'));
const GrowersDashboard = lazy(() => import('../components/Growers/GrowersDashboard'));
const FieldDetailsContainer = lazy(() => import('../components/FieldDetails/FieldDetailsContainer'));
const FieldSetupInfo = lazy(() => import('../components/auth2/FieldSetupInfo'));
const FarmOverviewContainer = lazy(() => import('../components/FarmOverview/FarmOverviewContainer'));

class App extends Component {
  /**
   * <App /> is the top level URI router for page components.
   * React-Router docs: https://reacttraining.com/react-router/web/guides/philosophy
   */
  constructor(props){
    super(props);
    this.goBack = this.goBack.bind(this);
  }

  goBack(){
    this.props.history.goBack();
  }
  componentDidMount() {
    if (this.props.isAuthenticated) {
      this.props.dispatch(getUser());
      if (sessionStorage.getItem('viewingUserId')){
        this.props.dispatch(getViewingUser());
      }
    }
  }

  render() {
    const {
      dispatch, history, isAuthenticated,
      messages, user, modal, isFetchingAuth,
      isViewOnly, adminView, unreleasedVisible
    } = this.props;

    const fullScreenRoutes = [
      'field-management', 'invite',
      'field-setup', 'register',
      'login', 'complete_registration'
    ];

    const newAppRoutes= [
      'growers','field-management', 'invite',
      'field-setup', 'register',
      'login', 'complete_registration', 'field-details',
      'farm-overview'
    ]

    const pathname = history.location.pathname;
    const isFullScreen = fullScreenRoutes.includes(pathname.split('/')[1]);
    const isNewApp = newAppRoutes.includes(pathname.split('/')[1]);
    const matchFieldDetails = matchPath(pathname, {
      path: `/field-details/:fieldId`,
    });
    const pathnameStyles = {
      [matchFieldDetails && matchFieldDetails.url]: {
        padding: 0,
      }
    }
    const overrideContainerStyle = pathnameStyles[pathname];

    const WrappedUnreleasedRoute = (props) => <UnreleasedRoute {...props} goBack ={this.goBack} />;

    return (
      <div className={`app ${isNewApp ? 'app-column-layout' : ''}`} style={{ position: 'relative' }}>
        {isViewOnly && <ViewOnlyBar viewedUser={user} />}
        {allowUnreleased && <UnreleasedVisibility dispatch={dispatch} visibility={unreleasedVisible}/>}
        <NavBar
          isAuthenticated={isAuthenticated}
          currentUser={user}
          appType={isNewApp && 'new'}
          unreleasedVisible={unreleasedVisible}
          dispatch={dispatch}
          pathname={pathname}
        />
        <StyledWrapper
          className={isFullScreen ? 'fullscreen' : ''}
          appType={isNewApp && 'new'}
          isAuthenticated={isAuthenticated}
        >
          <StyledContainer
            appType={isNewApp && 'new'}
            overrideStyle={overrideContainerStyle}
            className={`${isFullScreen ? 'fullscreen' : ''} ${isAuthenticated ? 'authenticated' : ''}`}
            appType={isNewApp && 'new'}
          >
            <FlashMessages />
            <Suspense fallback={<Loader height="20px" />}>
              <Switch>
                {/* Public Routes */}
                <Route
                  exact path='/'
                  render={() =>
                    isAuthenticated ? (
                      <Redirect
                        to={{
                          pathname: '/dashboard',
                          state: { from: this.props.location }
                        }}
                      />
                    ) : (
                      <Redirect
                        to={{
                          pathname: '/login',
                          state: { from: this.props.location }
                        }}
                      />
                    )
                  }
                />
                <Route
                  exact path='/login'
                  render={() => (
                    <Login2
                      onLoginClick={creds => dispatch(loginUser(creds))}
                      history={history}
                      dispatch={dispatch}
                      user={user}
                      isFetching={isFetchingAuth}
                    />
                  )}
                />
                <Route
                  exact path='/reset-password'
                  render={() => (
                    <ResetPassword
                      onResetClick={creds => dispatch(resetPassword(creds))}
                      hasErrors={this.props.hasResetPassErrors}
                      history={history}
                      dispatch={dispatch}
                      isFetching={isFetchingAuth}
                    />
                  )}
                />
                <Route
                  exact path='/complete_registration/:token?'
                  render={() => (
                    <MoreDetails
                      history={history}
                    />
                  )}
                />
                <Route
                  exact path='/register'
                  render={() => (
                    <Register2
                      history={history}
                    />
                  )}
                />
                {/* Private Routes */}
                <PrivateRoute
                  path='/my-account'
                  render={() => (
                    <AccountPage
                      user={user}
                      dispatch={dispatch}
                      history={history}
                    />
                  )}
                />
                <PrivateRoute
                  exact path='/submit'
                  render={() => <SamplesContainer key={`submit`} />}
                />
                <PrivateRoute
                  exact path='/view-samples/:submissionId?'
                  render={({ match }) => (
                    <SamplesContainer
                      submissionId={match.params.submissionId}
                      heading='Submission History'
                      key={`viewSamples-${match.params.submissionId}`}
                    />
                  )}
                />
                <PrivateRoute
                  exact path='/dashboard'
                  render={() => (
                    <Dashboard
                      user={user}
                      dispatch={dispatch}
                    />
                  )}
                />
                <PrivateRoute
                  exact path='/analytics'
                  render={() => <Analytics user={user} key='analytics' />}
                />
                <PrivateRoute
                  exact path='/analytics/:submissionId?'
                  render={({ match }) => (
                    <Analytics
                      submissionId={match.params.submissionId}
                      user={user}
                      key={`analytics-${match.params.submissionId}`}
                    />
                  )}
                />
                {/* Unreleased routes */}
                <WrappedUnreleasedRoute
                  exact path='/growers'
                  render={() => (
                    <GrowersDashboard
                      history={history}
                    />
                  )}
                />
                <WrappedUnreleasedRoute
                  path='/field-management'
                  render={() => (
                    <FieldManagementContainer
                      user={user}
                      dispatch={dispatch}
                      history={history}
                    />
                  )}
                />
                <WrappedUnreleasedRoute
                  exact path='/invite'
                  render={() => (
                    <InviteByEmail
                      user={user}
                      history={history}
                      getUser={() => dispatch(getUser())}
                    />
                  )}
                />
                <WrappedUnreleasedRoute
                  exact path='/field-setup'
                  render={() => (
                    <FieldSetupInfo
                      user={user}
                      history={history}
                    />
                  )}
                />
                <WrappedUnreleasedRoute
                  path='/farm-overview'
                  render={() => (
                    <FarmOverviewContainer
                      history={history}
                    />
                  )}
                />
                <WrappedUnreleasedRoute
                  exact path='/field-details/:fieldId'
                  render={() => <FieldDetailsContainer goBack={this.goBack} />}
                />
                <Route
                  exact path='/unauthorized'
                  render={() => (
                    <AccessError
                      statusCode={this.props.errorStatus}
                      history={history}
                    />
                  )}
                />
                <Route render={() => <NotFound goBack={this.goBack} />} />
              </Switch>
            </Suspense>
          </StyledContainer>
          {(!isNewApp && !isFullScreen) && <Footer isAuthenticated={isAuthenticated} />}
        </StyledWrapper>
      </div>
    )
  }
}


// TODO: use selectors from reducer to unpack state to props
function mapStateToProps(state) {
  // UpdatePassword form
  const updatePassFieldValue = formValueSelector('updatePassword')(state, 'newPassword1');
  const hasUpdatePassSyncErrors = getFormSyncErrors('updatePassword')(state);
  const hasUpdatePassSubmitErrors = getFormSubmitErrors('updatePassword')(state);
  const hasUpdatePassErrors = !!(
    (!!hasUpdatePassSyncErrors && (!!hasUpdatePassSyncErrors.oldPassword || !!hasUpdatePassSyncErrors.newPassword1 || !!hasUpdatePassSyncErrors.newPassword2)) ||
    (!!hasUpdatePassSubmitErrors && (!!hasUpdatePassSubmitErrors.oldPassword || !!hasUpdatePassSubmitErrors.newPassword1 || !!hasUpdatePassSubmitErrors.newPassword2))
  );
  // ResetPassword form
  const hasResetPassSyncErrors = getFormSyncErrors('reset')(state);
  const hasResetPassSubmitErrors = getFormSubmitErrors('reset')(state);
  const hasResetPassErrors = !!(
    (!!hasResetPassSyncErrors && !!hasResetPassSyncErrors.email) ||
    (!!hasResetPassSubmitErrors && !!hasResetPassSubmitErrors.email)
  );

  const { auth, messagesReducer } = state;
  const { isAuthenticated, user, isViewOnly } = auth;
  const { unreleasedVisible, errorStatus } = state.meta

  const isFetchingAuth = auth.isFetching;

  // props from state
  return {
    isAuthenticated,
    isFetchingAuth,
    user,
    unreleasedVisible,
    isViewOnly,
    // auth form errors
    hasUpdatePassErrors,
    hasResetPassErrors,
    // password field values, for matching
    updatePassFieldValue,
    errorStatus
  };
}

App.propTypes = {
  isAuthenticated: PropTypes.bool.isRequired,
  dispatch: PropTypes.func,
  user: PropTypes.object,
  history: PropTypes.object,
  messages: PropTypes.array,
  isFetchingAuth: PropTypes.bool,
  location: PropTypes.object,
  hasLoginErrors: PropTypes.bool,
  hasResetPassErrors: PropTypes.bool,
  isViewOnly: PropTypes.bool,
  errorStatus: PropTypes.number
};

export default withRouter(connect(mapStateToProps)(App));
